"use client";

import React, { useEffect, useRef, useState } from "react";
import { Play, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function EmpoweringYourStrategicGrowth() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="230301" id="empowering-students-to-master-mathematics" className="relative w-full overflow-hidden bg-background py-20 lg:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 z-0 opacity-[0.08] mix-blend-multiply dark:mix-blend-overlay bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680106/img/calculus_theorem_watermark.png')] bg-repeat" />

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="flex flex-col items-center lg:flex-row lg:items-stretch">
          
          {}
          <div ref={textRef} className={`flex w-full flex-col justify-center lg:w-[60%] lg:pr-12 xl:pr-24 transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <h1 className="font-serif text-[clamp(3rem,7vw,5rem)] font-bold leading-[1.05] tracking-tight text-primary">
              Math Mastery, Guided by{" "}
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent drop-shadow-sm">
                Experience.
              </span>
            </h1>
            
            <p className="mt-8 max-w-2xl font-default text-lg leading-relaxed text-muted-foreground md:text-xl">Welcome to Math with Mr. Cottone!<br /><br />With 17 years of teaching experience, Mr. Cottone offers personalized virtual tutoring that turns mathematical confusion into lasting understanding — and real confidence.<br /><br />Mr. Cottone specializes in math from 5th grade through college Calculus, tailoring every lesson to each student's specific course and learning style. He is available year-round for students who need extra support, those seeking enrichment beyond the classroom, and students preparing for standardized exams.<br /><br />Students consistently describe their sessions as both engaging and productive — many say working with Mr. Cottone has completely changed the way they feel about math.<br /> <br />Have questions? Please reach out! Mr. Cottone would love to help.<br /></p>
            
            
          </div>

          {}
          <div ref={imageRef} className={`relative mt-16 w-full lg:mt-0 lg:w-[40%] transition-all duration-1000 delay-300 ease-out ${isImageVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            {}
            <div className="relative z-20 w-full overflow-hidden rounded-lg shadow-3xl lg:-ml-16 xl:-ml-24 border border-border/50 bg-muted">
              <AspectRatio ratio={3 / 4}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680106/img/Web-Ad-photo-white-background.png" alt="Professional math tutor in a clean virtual office setting" className="h-full w-full object-cover transition-transform duration-1000 ![margin:32px_0px_32px_0px] ![padding:0px_56px_0px_56px] hover:scale-105" loading="lazy" decoding="async" src="https://buildingmathconfidence.com/wp-content/uploads/2026/06/mr-cottone-photo-white-bg.png" />
                {}
                <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent pointer-events-none" />
                
                {}
                <div className="absolute top-4 right-4 flex flex-col gap-2">
                  
                  
                </div>
              </AspectRatio>
            </div>
            
            {}
            <div className="absolute -bottom-8 -right-8 -z-10 h-full w-full rounded-lg bg-secondary/10 blur-3xl lg:-bottom-12 lg:-right-12" />
          </div>

        </div>
      </div>
    </section>;
}